import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import type { ShapeTextCharRunMap } from '../types/shape-text-layout-state'

/**
 * The run whose style applies at a caret: the character BEFORE it, falling back to the one after
 * it and then to the frame's seeded first run — the same rule a native caret follows, and the same
 * one `applyRunStyle`/`getEditingStyle` already use for what the next keystroke inherits.
 *
 * An empty frame has no character to read a run from, but its seeded first run still carries the
 * face the next keystroke will type in (WMPCM-11367). The same holds for an empty line after Enter:
 * the caret sits between two paragraph breaks, so it reads the seeded run of ITS paragraph (counted
 * by the breaks before it, which `buildCharRunMap` leaves as `null` entries), not the first one's.
 */
export function runAtCaret(
  charRunMap: ShapeTextCharRunMap,
  caretIndex: number,
  paragraphs: Paragraph[]
): TextRun | null {
  const caretParagraphIndex = charRunMap.slice(0, caretIndex).filter((run) => run === null).length

  return (
    charRunMap[Math.max(0, caretIndex - 1)] ??
    charRunMap[caretIndex] ??
    paragraphs[caretParagraphIndex]?.textLines?.[0]?.textRuns?.[0] ??
    paragraphs[0]?.textLines?.[0]?.textRuns?.[0] ??
    null
  )
}

/**
 * The caret is a fixed number of DEVICE pixels wide and sits on the device-pixel grid. A bare
 * `width: 1` in a zoom-scaled layer is 1.5 device pixels at a 1.5 DPR and lands at a fractional
 * x, so the browser anti-aliased it across two columns for some positions and one for others:
 * thin on an empty frame, thick once text pushed it to another x (WMPCM-11367).
 */
export function caretWidthPt(devicePixelRatio: number, canvasZoom: number): number {
  return Math.max(1, Math.round(devicePixelRatio)) / (devicePixelRatio * canvasZoom)
}

/** A font's ascent and descent at one point size, in points (`measureFontAscentDescentPt`). */
export interface FontInkPt {
  ascentPt: number
  descentPt: number
}

/**
 * How far below its top a row's baseline sits, for a row painted in a `line-height: rowHeight` box.
 *
 * The browser puts the baseline at `max over the row's boxes of (rowHeight - inkᵢ) / 2 + ascentᵢ`,
 * i.e. `rowHeight / 2 + (ascentᵢ - descentᵢ) / 2`, so the run with the largest ascent-minus-descent
 * (in practice the biggest one) sets it for every run on the row. Null for an empty list.
 */
export function rowBaselineOffsetPt(rowHeight: number, inks: readonly FontInkPt[]): number | null {
  if (inks.length === 0) return null

  return Math.max(...inks.map((ink) => rowHeight / 2 + (ink.ascentPt - ink.descentPt) / 2))
}

/**
 * The caret bar's vertical box within its row.
 *
 * Positioned on the row's own INK BOX, not on `yTop` plus a constant. The glyphs
 * beside the caret are painted by the browser into a `line-height: rowHeight` box,
 * which centres the font's `ascent + descent` inside that box and leaves
 * `(rowHeight - ink) / 2` of half-leading above it. This used to read
 * `top: yTop + 2, height: max(8, rowHeight - 4)` — two constants that happen to
 * approximate the ink box at one particular size and leading and drift away from it
 * at every other, so the bar sat visibly off the line the text was on (QA's "the
 * caret and the text plane are not on the same line, the caret appears slightly
 * above", WMPCM-10123). Deriving both ends from the same metrics the browser used
 * makes the bar span exactly the glyphs' own extent at any size, font and leading,
 * rather than at one of them.
 *
 * Then clamped into `[yTop + 1, yTop + rowHeight - 1]` — the interval
 * `bandSegmentsAt` (`lib/shapes/shape-text.ts`) actually probed when it decided this
 * row's segments sit inside the outline. The caret is a child of the outline
 * `clipPath`, so anything past that band is liable to be sliced on a narrowing
 * outline — a star's lower arms cut the old unconditional 8px floor in half, which
 * is the other half of the same QA report. The clamp only binds when the leading is
 * tight enough that the ink itself leaves the band, which is a case the layout
 * already documents as allowed to overlap (`MIN_LEADING_FRACTION_OF_NATURAL` is a
 * damage bound, not a no-overlap guarantee) — there the bar gives up its overhang
 * and stays whole instead.
 *
 * Do NOT "fix" a residual slice by hoisting the caret out of the clipped container:
 * that reopens WMPCM-9070 (content painting outside a star/heart and over its
 * neighbours), which is the whole reason the clip is there.
 *
 * `baselinePt` is where the row's shared baseline sits below its top (`rowBaselineOffsetPt`).
 * Every run on a row sits on that one baseline, so the caret's own ink box hangs from it
 * (`baselinePt - ascent`). Centring the caret run's ink in the row, which is what omitting
 * it does, is only right when that run is also the row's tallest: in an 18pt run on a row
 * holding 72pt text the bar floated about a text height above the glyphs, matching neither
 * run (WMPCM-11367).
 */
export function caretVerticalBox(
  caret: { yTop: number; rowHeight: number },
  ink: FontInkPt,
  shapeHeight: number,
  baselinePt?: number
): { top: number; height: number } {
  const inkHeightPt = ink.ascentPt + ink.descentPt
  const inkTop =
    baselinePt === undefined ? (caret.rowHeight - inkHeightPt) / 2 : baselinePt - ink.ascentPt
  const top = Math.max(1, inkTop)
  const bottom = Math.min(caret.rowHeight - 1, inkTop + inkHeightPt)

  return {
    top: caret.yTop + top,
    // The `shape.height - 4` clamp is a separate concern and stays — it keeps a very
    // short shape from drawing the caret as a stub poking out of the overlay box.
    height: Math.min(Math.max(1, bottom - top), Math.max(2, shapeHeight - 4)),
  }
}
